import React, { useState } from 'react';
import {
  Typography,
  Box,
  makeStyles,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import AICOmponent from './GenerateAdForm';
import AdPreview from './AdPreview';

// Interfaces for GenerateAds component
export interface GenerateAdsProps {
  assistantDetails?: any;
  changeState?: (field: string, value: any) => void;
}

// Interface for image generation response data
export interface ImageGenerationResponse extends MultipleImageResult {
  userPrompt?: string;
  rebuiltQuery?: string;
  text?: string;
  caption?: string;
  headline?: string;
  pageDetails?: PageDetails;
  linkMetaData?: LinkMetaData;
  previewLink?: string;
  selectedImage?: string;
  selectedIndex?: number;
}

export interface ImageResult {
  created: string;
  data: Array<{
    is_image_safe: boolean;
    prompt: string;
    resolution: string;
    seed: number;
    style_type: string | null;
    url: string;
  }>;
}

export interface MultipleImageResult {
  imageResult1?: ImageResult;
  imageResult2?: ImageResult;
  imageResult3?: ImageResult;
  imageResults?: ImageResult[]; // For when multiple results come as an array
}

export interface PageDetails {
  picture?: string;
  pageName?: string;
}

export interface LinkMetaData {
  linkTitle?: string;
  linkDescription?: string;
  linkImage?: string;
}

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    gap: '30px',
    marginBottom: '50px',
    paddingLeft: '8%',
    paddingRight: '10%',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    // [theme.breakpoints.down('sm')]: {
    //   flexDirection: 'column',
    //   paddingLeft: '5%',
    //   paddingRight: '5%',
    //   gap: '20px',
    // },
    height: '100%',
  },
  aiContainer: {
    display: 'flex',
    // paddingLeft: '20px',
    [theme.breakpoints.down('sm')]: {
      paddingLeft: 0,
      width: '100%',
    },
  },
  previewContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    paddingTop: '18px',
    marginBottom: '20px',
    minWidth: '300px',
    width: '100%',
    maxWidth: '1113px',
    [theme.breakpoints.down('sm')]: {
      paddingRight: 0,
      paddingTop: '20px',
      width: '100%',
      minWidth: 'auto',
    },
  },
  iconBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#deffe7',
    borderRadius: '6px',
    padding: '8px',
  },
  headerBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '2px',
    paddingTop: '2px',
  },
  previewImage: {
    width: '100%',
    // maxHeight: 500,
    objectFit: 'cover',
    borderRadius: '8px',
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
  },
  aiHeaderText: {
    fontWeight: 500,
    fontSize: '72px',
    marginBottom: '4px',
    color: '#000000',
    [theme.breakpoints.down('sm')]: {
      fontSize: '20px',
    },
  },
}));

export default function GenerateAds(props: GenerateAdsProps) {
  const { assistantDetails, changeState } = props;
  const classes = useStyles();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [aiModalOpen, setAiModalOpen] = useState<boolean>(false);
  const [state, setState] = useState<any>({});
  const [responseData, setResponseData] = useState<ImageGenerationResponse>({});

  const handleSubmit = (newState: any) => {
    setState(newState);
    // Reset response data when starting a new generation
    setResponseData({});
  };

  const handleCloseModal = () => {
    setAiModalOpen(false);
  };

  const handleImageGeneration = (data: any) => {
    setResponseData((prevData: ImageGenerationResponse) => {
      // Handle streaming updates - merge new data with existing data
      const mergedData: ImageGenerationResponse = { ...prevData };

      // Merge image results
      if (data.imageResult1) {
        mergedData.imageResult1 = data.imageResult1;
      }

      // Handle multiple image results
      if (data.imageResults) {
        mergedData.imageResults = data.imageResults;
      }

      // Handle selected image and index
      if (data.selectedImage) {
        mergedData.selectedImage = data.selectedImage;
      }
      if (data.selectedIndex !== undefined) {
        mergedData.selectedIndex = data.selectedIndex;
      }

      // Merge other properties
      if (data.text) mergedData.text = data.text;
      if (data.caption) mergedData.caption = data.caption;
      if (data.headline) mergedData.headline = data.headline;
      if (data.pageDetails) mergedData.pageDetails = data.pageDetails;
      if (data.linkMetaData) mergedData.linkMetaData = data.linkMetaData;
      if (data.previewLink) mergedData.previewLink = data.previewLink;

      return mergedData;
    });
  };

  console.log('response', responseData);

  return (
    <Box className={classes.root}>
      <Box className={classes.aiContainer}>
        <Box style={{ backgroundColor: 'white' }} mt={4}>
          <Box className={classes.headerBox} mb={3}>
            <Box className={classes.iconBox}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width={48}
                height={48}
                fill="#0a474c"
                viewBox="0 0 25 25"
              >
                <path
                  d="M6.37954 14.0132L10.1522 12.1269C11.2843 11.5608 12.2022 10.6429 12.7682 9.51079L14.6553 5.73745C14.716 5.616 14.8094 5.51386 14.9249 5.44247C15.0404 5.37108 15.1735 5.33327 15.3093 5.33327C15.4451 5.33327 15.5782 5.37108 15.6937 5.44247C15.8092 5.51386 15.9025 5.616 15.9633 5.73745L17.8497 9.51012C18.4157 10.6422 19.3336 11.5602 20.4657 12.1263L24.2377 14.0132C24.3592 14.0739 24.4614 14.1672 24.5328 14.2827C24.6042 14.3983 24.642 14.5314 24.642 14.6672C24.642 14.803 24.6042 14.9361 24.5328 15.0516C24.4614 15.1672 24.3592 15.2605 24.2377 15.3212L20.465 17.2076C19.3329 17.7736 18.415 18.6915 17.849 19.8236L15.9626 23.5963C15.9019 23.7177 15.8085 23.8199 15.693 23.8912C15.5775 23.9626 15.4444 24.0004 15.3086 24.0004C15.1728 24.0004 15.0397 23.9626 14.9242 23.8912C14.8087 23.8199 14.7153 23.7177 14.6546 23.5963L12.7682 19.8236C12.2022 18.6915 11.2843 17.7736 10.1522 17.2076L6.37954 15.3205C6.25822 15.2598 6.15621 15.1664 6.08491 15.051C6.01362 14.9355 5.97586 14.8025 5.97586 14.6669C5.97586 14.5312 6.01362 14.3982 6.08491 14.2827C6.15621 14.1673 6.25822 14.074 6.37954 14.0132ZM6.85181 5.08932L8.46861 4.27985C8.52052 4.25378 8.56417 4.21379 8.59466 4.16434C8.62516 4.1149 8.64131 4.05795 8.64131 3.99985C8.64131 3.94176 8.62516 3.88481 8.59466 3.83537C8.56417 3.78592 8.52052 3.74593 8.46861 3.71985L6.85181 2.91119C6.36661 2.66862 5.97318 2.27519 5.73061 1.78999L4.92194 0.173188C4.89587 0.121275 4.85588 0.0776322 4.80643 0.0471354C4.75699 0.0166385 4.70004 0.000488281 4.64194 0.000488281C4.58385 0.000488281 4.5269 0.0166385 4.47746 0.0471354C4.42801 0.0776322 4.38802 0.121275 4.36194 0.173188L3.55328 1.78999C3.31071 2.27519 2.91728 2.66862 2.43208 2.91119L0.815278 3.71985C0.763365 3.74593 0.719722 3.78592 0.689225 3.83537C0.658728 3.88481 0.642578 3.94176 0.642578 3.99985C0.642578 4.05795 0.658728 4.1149 0.689225 4.16434C0.719722 4.21379 0.763365 4.25378 0.815278 4.27985L2.43221 5.08839C2.9174 5.33092 3.31082 5.7243 3.55341 6.20946L4.36194 7.82652C4.38802 7.87844 4.42801 7.92208 4.47746 7.95257C4.5269 7.98307 4.58385 7.99922 4.64194 7.99922C4.70004 7.99922 4.75699 7.98307 4.80643 7.95257C4.85588 7.92208 4.89587 7.87844 4.92194 7.82652L5.73034 6.20959C5.97303 5.72444 6.36655 5.33178 6.85181 5.08932Z"
                  fill={'#41b862'}
                />
              </svg>
            </Box>
            <Box ml={2}>
              <Typography variant="h3" className={classes.aiHeaderText}>
                Generate Ad Creative with AI
              </Typography>
              {/* <Typography variant="body2" style={{ color: '#6E6E6E' }}>
                Create customized variations with AI
              </Typography> */}
            </Box>
          </Box>
          <AICOmponent
            selectedTemplate={null}
            onSubmit={handleSubmit}
            currentState={state}
            closeModal={handleCloseModal}
            assistantDetails={assistantDetails || {}}
            onImageGeneration={handleImageGeneration}
          />
        </Box>
      </Box>

      <Box className={classes.previewContainer}>
        {responseData?.imageResult1?.data?.[0]?.url ? (
          <Box>
            <img
              src={responseData.imageResult1.data[0].url}
              alt="AI Generated Image 1"
              className={classes.previewImage}
            />
          </Box>
        ) : (
          <Box>
            <img
              src={
                'https://whatsapp-ai-assets.s3.amazonaws.com/generated-images/bbad94c1-c8bb-47fc-94d0-3e32e05a0b7a_1.png'
              }
              alt="AI Generated Image"
              className={classes.previewImage}
            />
          </Box>
        )}
        {/* {responseData?.imageResult2?.data?.[0]?.url ? (
          <Box>
            <img
              src={responseData.imageResult2.data[0].url}
              alt="AI Generated Image 2"
              className={classes.previewImage}
            />
          </Box>
        ) : (
          <Box>
            <img
              src={
                'https://whatsapp-ai-assets.s3.amazonaws.com/generated-images/cae21759-1c22-441b-9ff6-a805cb734b3f_1.png'
              }
              alt="AI Generated Image"
              className={classes.previewImage}
            />
          </Box>
        )} */}
      </Box>

      {/* <Box className={classes.previewContainer}>
        <AdPreview isFromFlowKeyword={true} payload={responseData} />
      </Box> */}
    </Box>
  );
}
